import { clearAnalyticsStorage } from "~/lib/analytics/clear-storage";
import { HELD_PREV_PATHNAME, HELD_TITLE } from "~/lib/analytics/held-page-context";
import { sanitizeCapturedEvent } from "~/lib/analytics/sanitize-url";
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from "react";

import {
  captureEvent as captureClientEvent,
  getDistinctId,
  initAnalytics,
  loadAnalytics,
  optOutAnalytics,
  syncIdentity,
} from "~/lib/analytics/client";
import {
  withSegmentation,
  type AnalyticsEnvironment,
  type EventProperties,
  type Market,
} from "~/lib/analytics/events";
import {
  CONSENT_CHANGE_EVENT,
  hasConsent,
  parseConsent,
  readRawConsent,
  type ConsentState,
} from "~/lib/consent";

/**
 * T78 — the bootstrap T55 never had.
 *
 * T55 shipped `app/lib/analytics/client.ts` but nothing ever called `initAnalytics`, so
 * PostHog's `initialised` flag stayed `false` and every client-side capture silently
 * no-op'd. This provider is the missing call site. It follows the `PwaProvider`
 * precedent (`app/components/pwa/PwaProvider.tsx`): a mounted-in-root effect component,
 * not a presentational one, so the `app/components` "pure presentational" rule does not
 * apply to it any more than it does to PwaProvider.
 *
 * Consent is the gate, not an afterthought: the T317 banner's `analytics` category is a
 * separate legal basis from the lead-capture consent, so nothing initialises and nothing
 * captures until she has said yes to analytics specifically.
 */

/** Ceiling on the pre-bootstrap buffer. See `capture` for why it is effectively never hit. */
const MAX_PENDING_EVENTS = 20;
/** Event, properties, its account, the page view a `$pageview` counts (else null), and when it happened. */
type PendingEvent = [string, EventProperties, string | null, number | null, number];

interface AnalyticsApi {
  /** No-op unless analytics consent is granted and PostHog initialised. */
  capture: (event: string, properties?: EventProperties) => void;
  /** PostHog's anonymous id, for stitching the worker half of a funnel. */
  distinctId: () => string | null;
  /** Whether analytics consent is currently granted. */
  analyticsAllowed: boolean;
}

const AnalyticsContext = createContext<AnalyticsApi>({
  capture: () => {},
  distinctId: () => null,
  analyticsAllowed: false,
});

/**
 * Consent-aware capture for any surface under the root layout. Safe to call
 * unconditionally — it degrades to a no-op rather than throwing when consent is absent,
 * which is what lets call sites read as plain instrumentation instead of as a
 * permissions dance.
 */
export function useAnalytics(): AnalyticsApi {
  return useContext(AnalyticsContext);
}

export function AnalyticsProvider({
  posthogKey,
  market,
  environment,
  userId = null,
  pathname,
  children,
}: {
  posthogKey: string | null;
  /** T78 — derived server-side in the root loader; only the server sees the country. */
  market: Market;
  /** T78 — which deployment this is, so preview QA can be filtered out of real numbers. */
  environment: AnalyticsEnvironment;
  /**
   * T817 — the signed-in member's internal id (root loader), or null when signed out.
   * The same pseudonymous id the server emits under, so browser and server halves of her
   * journey are one person in PostHog. Never the email.
   */
  userId?: string | null;
  /**
   * T817 — the current route pathname (root `useLocation`). A change is one `$pageview`;
   * passed in rather than read here so the provider stays renderable outside a router.
   */
  pathname?: string;
  children: ReactNode;
}) {
  const [consent, setConsent] = useState<ConsentState | null>(null);

  // `consentRead` is NOT redundant with `consent`: `parseConsent` returns null both for
  // "no cookie" and for "not read yet", and those two mean opposite things for the buffer
  // below — one is a decision to respect, the other is a decision that has not happened.
  const [consentRead, setConsentRead] = useState(false);
  // Same fact as `consentRead`, in a ref, because `capture`'s closure must know whether a
  // decision EXISTS without waiting for a re-render. Declared here beside the state it
  // mirrors rather than further down, so the effect below is not mutating something a
  // reader has not met yet.
  const consentReadRef = useRef(false);

  // Captures made before PostHog is live, held until the bootstrap resolves (see below).
  const pendingRef = useRef<PendingEvent[]>([]);
  const readyRef = useRef(false);
  const loadingRef = useRef(false);
  const consentRef = useRef<ConsentState | null>(null);
  const consentGenerationRef = useRef(0);

  // Consent is read on the CLIENT, deliberately not in the root loader, for two reasons
  // that survive scrutiny: the service worker may re-serve a cached navigation document
  // when the network fails, so a baked-in consent value could be replayed after she
  // changed it; and consent changes MID-VISIT on surfaces that never reload (the
  // auto-test is one continuous surface), which a loader-derived value cannot follow.
  //
  // (An earlier revision justified this as "the shell cache would hand one visitor's
  // consent to the next". That was wrong — `cacheStrategy` is the service worker, which
  // is per browser, not a shared edge cache. The decision stands; the reason did not.)
  // T817 — declared BEFORE the consent read on purpose. Effects run in declaration order,
  // so the mount pageview can enter the buffer before the cookie is read. Later
  // pageviews can also wait while a consented SDK download is in flight.
  const userIdRef = useRef(userId);
  userIdRef.current = userId;
  useEffect(() => {
    // Login, logout and account switch after PostHog is live. Before that, the init
    // effect below identifies before it flushes the buffer.
    pendingRef.current = pendingRef.current.filter((entry) => entry[2] === userId);
    if (readyRef.current) syncIdentity(userId);
  }, [userId]);

  // One `$pageview` per effective navigation: keyed on the pathname, so hydration,
  // re-renders and loader revalidation (same path) never add a second one. The query is
  // not part of the key — and is stripped from the URL before sending (`sanitize-url.ts`).
  // Goes through `capture`, so it inherits the consent contract exactly: held while
  // consent is unread, dropped once a refusal is read, never replayed after a later grant.
  const lastPageviewPathRef = useRef<string | null>(null);
  /** One id per page view (a pathname change), so a page revisited counts again (T975). */
  const viewIdRef = useRef(0);
  /** The analytics grant happened in this page and has not reached the SDK wrapper yet. */
  const grantedInPageRef = useRef(false);
  /** The page view whose pageview last reached the SDK. */
  const sentViewIdRef = useRef<number | null>(null);
  const captureRef = useRef<(event: string, properties?: EventProperties) => void>(() => {});
  useEffect(() => {
    if (pathname === undefined || lastPageviewPathRef.current === pathname) return;
    lastPageviewPathRef.current = pathname;
    viewIdRef.current++;
    captureRef.current("$pageview", {});
  }, [pathname]);

  useEffect(() => {
    const read = () => {
      const next = parseConsent(readRawConsent(document.cookie));
      // A grant made in this page (not the cookie read at mount, not a re-save that leaves
      // analytics on) is a new decision: the wrapper sends `$opt_in` for it (T975).
      if (consentReadRef.current && !hasConsent("analytics", consentRef.current) && hasConsent("analytics", next)) {
        grantedInPageRef.current = true;
      }
      consentRef.current = next;
      // Invalidate pending work synchronously, including revoke/re-grant in one React batch.
      consentGenerationRef.current++;
      if (!hasConsent("analytics", next)) {
        readyRef.current = false;
        loadingRef.current = false;
        pendingRef.current = [];
        optOutAnalytics();
        // After the SDK's own opt-out write: no analytics identifier survives a refusal (T975).
        clearAnalyticsStorage();
      }
      setConsent(next);
      setConsentRead(true);
      consentReadRef.current = true;
    };
    read();
    window.addEventListener(CONSENT_CHANGE_EVENT, read);
    return () => {
      window.removeEventListener(CONSENT_CHANGE_EVENT, read);
      // The generation bump is what stops a late load from flushing; the queue is NOT
      // emptied here. StrictMode (development) runs this cleanup and re-runs the effects,
      // and the held mount pageview must survive that; a refusal empties it in `read`.
      consentGenerationRef.current++;
      readyRef.current = false;
      loadingRef.current = false;
      optOutAnalytics();
    };
  }, []);

  const analyticsAllowed = hasConsent("analytics", consent);

  // The pre-bootstrap buffer (`pendingRef`, declared above so the T817 effects can reach
  // it). Why it is needed at all: React runs CHILD effects before PARENT effects, so a
  // surface that captures from its own mount effect does so before this provider has read
  // the cookie or called `initAnalytics`. Without the buffer that event is dropped
  // silently — which is exactly what happened to `auto_test_result_viewed` on the
  // `?r=` deep link, the one event the T285 email attribution depends on. Buffering here
  // rather than gating each call site is deliberate: the next mount-time capture would
  // otherwise walk into the same trap.
  useEffect(() => {
    // Nothing decided yet — keep buffering rather than treating "unread" as "refused".
    if (!consentRead) return;

    if (!posthogKey || !analyticsAllowed) {
      optOutAnalytics();
      readyRef.current = false;
      loadingRef.current = false;
      // DISCARD, never hold. The buffer bridges "consent existed but had not been read
      // yet"; it must not become "collect now, send if she consents later", which would
      // transmit events that happened before any legal basis for them existed.
      pendingRef.current = [];
      return;
    }

    let cancelled = false;
    const generation = consentGenerationRef.current;
    loadingRef.current = true;
    // The page view she is on when capture is authorised counts, once, and is queued NOW:
    // judged when the download ended, a click during it lost the grant page (T975). A view
    // already sent, or already held (consent read at mount), is not queued again.
    const view = viewIdRef.current;
    const held = pendingRef.current.some(([event, , , heldView]) => event === "$pageview" && heldView === view);
    if (lastPageviewPathRef.current !== null && sentViewIdRef.current !== view && !held) {
      captureRef.current("$pageview", {});
    }
    const current = () => !cancelled && generation === consentGenerationRef.current
      && hasConsent("analytics", consentRef.current);
    const initialise = () => {
      if (!current()) return;
      const grantedInThisPage = grantedInPageRef.current;
      initAnalytics(posthogKey, consentRef.current, userIdRef.current, environment, { grantedInThisPage });
      grantedInPageRef.current = false;
      syncIdentity(userIdRef.current);
      readyRef.current = true;
      loadingRef.current = false;
      const queued = pendingRef.current;
      pendingRef.current = [];
      // The SDK chains pageviews by the page it is on when it sends, so a flushed pageview
      // names its predecessor from THIS flush — only events that are sent, never a refused
      // interval (emptied on refusal) or another account's (filtered here: the account-change
      // effect has not run yet when the download resolves between that render and its effects).
      let previousPath: string | null = null;
      for (const [event, properties, capturedUserId, view, at] of queued) {
        if (capturedUserId !== userIdRef.current) continue;
        const timestamp = new Date(at);
        if (event === "$pageview") {
          const sent = previousPath ? { ...properties, [HELD_PREV_PATHNAME]: previousPath } : properties;
          captureClientEvent(event, sent, consentRef.current, { timestamp });
          previousPath = typeof properties.$pathname === "string" ? properties.$pathname : null;
          sentViewIdRef.current = view;
        } else {
          captureClientEvent(event, properties, consentRef.current, { timestamp });
        }
      }
    };
    const failed = () => {
      if (!current()) return;
      loadingRef.current = false;
      pendingRef.current = [];
    };
    const pending = loadAnalytics();
    if (pending) void pending.then(initialise).catch(failed);
    else initialise();
    return () => { cancelled = true; };

  }, [posthogKey, analyticsAllowed, consent, consentRead, environment]);

  const capture = useCallback(
    (event: string, properties: EventProperties = {}) => {
      // `market` and `is_legacy` are attached HERE, once, rather than at each call site.
      // With a single price across very different purchasing powers, an un-segmented rate
      // is misleading rather than merely coarse — and a segmentation property present on
      // some events but not others produces charts that quietly disagree. Making it
      // structurally impossible to forget is the point.
      const enriched = withSegmentation(properties, market, environment);
      if (!readyRef.current) {
        // Hold unknown-at-mount or currently authorized loading activity, never refusal.
        // Identity is captured now: a later account switch cannot claim this queue.
        const mayBuffer = !consentReadRef.current || (loadingRef.current && hasConsent("analytics", consentRef.current));
        if (mayBuffer && pendingRef.current.length < MAX_PENDING_EVENTS) {
          // The SDK reads location at capture time. Preserve the originating route
          // before a delayed flush; sanitize before retaining any URL in memory.
          // A pageview's title too, which the SDK reads at send time: it rides as a held
          // value the client's `before_send` restores (`restoreHeldPageContext`).
          const snapshot = sanitizeCapturedEvent({ properties: {
            ...enriched,
            ...(event === "$pageview" ? { [HELD_TITLE]: document.title } : {}),
            $current_url: window.location.href,
            $pathname: window.location.pathname,
          } }).properties;
          pendingRef.current.push([event, snapshot, userIdRef.current, event === "$pageview" ? viewIdRef.current : null, Date.now()]);
        }
        return;
      }
      if (!hasConsent("analytics", consentRef.current)) return;
      captureClientEvent(event, enriched, consentRef.current);
      if (event === "$pageview") sentViewIdRef.current = viewIdRef.current;
    },
    [consent, market, environment],
  );
  captureRef.current = capture;

  const value = useMemo<AnalyticsApi>(
    () => ({ capture, distinctId: getDistinctId, analyticsAllowed }),
    [capture, analyticsAllowed],
  );

  return <AnalyticsContext.Provider value={value}>{children}</AnalyticsContext.Provider>;
}
